// app/javascript/controllers/lots/index_map_controller.js

import { Controller } from "@hotwired/stimulus";
import {
  createMap,
  fitBounds,
  addReadOnlyGeomanControls,
  createLotLabel,
} from "../../helpers/map_helpers.js";
import { escapeHtml } from "../../utils.js";

const FALLBACK_COLOR = "#9CA3AF";
const FALLBACK_OPACITY = 0.35;
const TAG_FILL_OPACITY = 0.7;
const METRIC_OPACITY = 0.65;
// Five discrete lightness steps for the tag-spend quintile ramp. Light → dark
// matches "less → more" spend. Picked to read clearly on satellite imagery.
const TAG_LIGHTNESS_STEPS = [78, 65, 52, 42, 32];

// Connects to data-controller="lots--index-map"
export default class extends Controller {
  static targets = [
    "map",
    "mapWrapper",
    "metricPill",
    "tagPill",
    "tagPillLabel",
    "tagDropdownItem",
    "tagRow",
    "tagSearch",
    "fullscreenIconOpen",
    "fullscreenIconClose",
  ];

  static values = {
    lots: Array,
    labels: Object,
    tags: Array,
    farmLat: Number,
    farmLon: Number,
    activeLayer: { type: String, default: "net_result" },
    activeTagId: { type: String, default: "" },
  };

  connect() {
    this.tagsById = this.tagsValue.reduce((acc, tag) => {
      acc[String(tag.id)] = tag;
      return acc;
    }, {});

    this.map = createMap(this.mapTarget, {
      lat: this.farmLatValue || 0,
      lon: this.farmLonValue || 0,
      zoom: 15,
    });

    this.#createHoverCard();

    this.polygons = this.lotsValue.map((lot) => {
      const geojson = JSON.parse(lot.polygon);
      const polygon = L.geoJSON(geojson, {
        style: { fillOpacity: METRIC_OPACITY, weight: 2 },
      }).addTo(this.map);

      polygon.on("mouseover", () => this.#showHoverCard(lot));
      polygon.on("mousemove", (event) => this.#positionHoverCard(event));
      polygon.on("mouseout", () => this.#hideHoverCard());
      polygon.on("click", () => {
        window.location.href = lot.url;
      });

      const label = this.#shouldShowLotLabel(lot)
        ? createLotLabel(
            this.map,
            lot.name,
            polygon,
            lot.color,
            lot.weekLabel ? escapeHtml(lot.weekLabel) : null,
          )
        : null;

      return { lot, polygon, label };
    });

    if (this.polygons.length > 0) {
      fitBounds(
        this.map,
        this.polygons.map((p) => p.polygon),
      );
    }

    addReadOnlyGeomanControls(this.map);

    this.#applyStyles();
  }

  disconnect() {
    this.hoverCardEl?.remove();
    this.polygons?.forEach(({ label }) => label?.remove?.());
    if (this.escHandler) {
      document.removeEventListener("keydown", this.escHandler);
      this.escHandler = null;
    }
    document.body.classList.remove("overflow-hidden");
  }

  selectMetric(event) {
    const metric = event.params.metric;
    if (!metric) return;
    this.activeLayerValue = metric;
    this.activeTagIdValue = "";
    this.#applyStyles();
  }

  selectTag(event) {
    const tagId = String(event.params.tagId ?? "");
    if (!tagId || !this.tagsById[tagId]) return;
    this.activeLayerValue = "tag";
    this.activeTagIdValue = tagId;
    this.#applyStyles();
  }

  filterTags(event) {
    const query = event.target.value.trim().toLowerCase();
    this.tagDropdownItemTargets.forEach((item) => {
      const text = item.dataset.tagSearchText || "";
      item.classList.toggle("hidden", query !== "" && !text.includes(query));
    });
  }

  toggleFullscreen() {
    if (!this.hasMapWrapperTarget) return;
    const wrapper = this.mapWrapperTarget;
    const isFullscreen = wrapper.classList.toggle("lots-map-fullscreen");
    if (this.hasFullscreenIconOpenTarget)
      this.fullscreenIconOpenTarget.classList.toggle("hidden", isFullscreen);
    if (this.hasFullscreenIconCloseTarget)
      this.fullscreenIconCloseTarget.classList.toggle("hidden", !isFullscreen);
    document.body.classList.toggle("overflow-hidden", isFullscreen);
    // Leaflet needs to recompute size after the container resizes
    requestAnimationFrame(() => this.map.invalidateSize());

    if (isFullscreen) {
      this.escHandler = (event) => {
        if (event.key === "Escape") this.toggleFullscreen();
      };
      document.addEventListener("keydown", this.escHandler);
    } else if (this.escHandler) {
      document.removeEventListener("keydown", this.escHandler);
      this.escHandler = null;
    }
  }

  // ===== Stimulus value-change callbacks =====

  activeLayerValueChanged() {
    this.#updatePillStyles();
  }

  activeTagIdValueChanged() {
    this.#updatePillStyles();
  }

  lotsValueChanged() {
    this.tagSortedSpendsCache = {};
  }

  // ===== private =====

  #updatePillStyles() {
    const activeLayer = this.activeLayerValue;
    const activeTagId = this.activeTagIdValue;

    this.metricPillTargets.forEach((pill) => {
      const isActive = pill.dataset.metric === activeLayer;
      pill.classList.toggle("btn-primary", isActive);
      pill.classList.toggle("btn-ghost", !isActive);
    });

    if (this.hasTagPillTarget) {
      const tagActive = activeLayer === "tag";
      this.tagPillTarget.classList.toggle("btn-primary", tagActive);
      this.tagPillTarget.classList.toggle("btn-ghost", !tagActive);
    }

    if (this.hasTagPillLabelTarget) {
      const prefix = this.labelsValue.pill.by_tag_prefix;
      const placeholder = this.labelsValue.pill.by_tag_placeholder;
      if (activeLayer === "tag" && this.tagsById[activeTagId]) {
        this.tagPillLabelTarget.textContent = `${prefix}: ${this.tagsById[activeTagId].name}`;
      } else {
        this.tagPillLabelTarget.textContent = `${prefix}: ${placeholder}`;
      }
    }

    this.tagRowTargets.forEach((row) => {
      const isActive =
        activeLayer === "tag" && row.dataset.tagId === activeTagId;
      row.classList.toggle("bg-primary/10", isActive);
    });

    if (this.hasTagDropdownItemTarget) {
      this.tagDropdownItemTargets.forEach((item) => {
        const isActive =
          activeLayer === "tag" && item.dataset.tagId === activeTagId;
        item.classList.toggle("bg-primary/10", isActive);
      });
    }
  }

  #applyStyles() {
    this.polygons.forEach(({ lot, polygon }) => {
      polygon.setStyle(this.#styleFor(lot));
    });
    if (this.activeHoverLot) {
      this.hoverCardEl.innerHTML = this.#renderTooltipHtml(this.activeHoverLot);
    }
  }

  #shouldShowLotLabel(lot) {
    return Boolean(lot.name);
  }

  #createHoverCard() {
    const el = document.createElement("div");
    el.className = "hidden lot-map-hover-card";
    el.style.cssText = [
      "position: fixed",
      "pointer-events: none",
      "z-index: 10000",
      "background: hsl(0 0% 100%)",
      "border: 1px solid hsl(220 13% 91%)",
      "border-radius: 8px",
      "box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12)",
      "padding: 10px 12px",
      "max-width: 280px",
      "transition: opacity 80ms ease-out",
      "opacity: 0",
    ].join(";");
    document.body.appendChild(el);
    this.hoverCardEl = el;
  }

  #showHoverCard(lot) {
    this.activeHoverLot = lot;
    this.hoverCardEl.innerHTML = this.#renderTooltipHtml(lot);
    this.hoverCardEl.classList.remove("hidden");
    this.hoverCardEl.style.opacity = "1";
  }

  #hideHoverCard() {
    this.activeHoverLot = null;
    this.hoverCardEl.classList.add("hidden");
    this.hoverCardEl.style.opacity = "0";
  }

  #positionHoverCard(event) {
    const orig = event.originalEvent;
    if (!orig) return;
    const offsetX = 14;
    const offsetY = 14;
    const cardWidth = this.hoverCardEl.offsetWidth;
    const cardHeight = this.hoverCardEl.offsetHeight;
    let left = orig.clientX + offsetX;
    let top = orig.clientY + offsetY;
    if (left + cardWidth > window.innerWidth - 8) {
      left = orig.clientX - cardWidth - offsetX;
    }
    if (top + cardHeight > window.innerHeight - 8) {
      top = orig.clientY - cardHeight - offsetY;
    }
    if (left < 8) left = 8;
    if (top < 8) top = 8;
    this.hoverCardEl.style.left = `${left}px`;
    this.hoverCardEl.style.top = `${top}px`;
  }

  #styleFor(lot) {
    const layer = this.activeLayerValue;

    if (layer === "tag") {
      const tagId = this.activeTagIdValue;
      const tag = this.tagsById[tagId];
      const cost = (lot.tag_costs && lot.tag_costs[tagId]) || 0;

      if (cost <= 0 || !tag) {
        return {
          fillColor: FALLBACK_COLOR,
          color: FALLBACK_COLOR,
          fillOpacity: FALLBACK_OPACITY,
          weight: 2,
        };
      }
      const bucket = this.#tagQuintile(tagId, cost);
      const color = tagColorAtBucket(tag.color, bucket);
      return {
        fillColor: color,
        color,
        fillOpacity: TAG_FILL_OPACITY,
        weight: 2,
      };
    }

    // `fillColor` is the Lot Stage color on sown seasonal lots (ADR 0065).
    // Report layers still win, so the P&L map is unaffected.
    const color =
      lot.layers?.[layer]?.color || lot.fillColor || lot.color || FALLBACK_COLOR;
    return { fillColor: color, color, fillOpacity: METRIC_OPACITY, weight: 2 };
  }

  // Returns 0-4 quintile bucket index for `cost` against all positive spends
  // on `tagId`. Cached per tag so we don't sort on every restyle.
  #tagQuintile(tagId, cost) {
    if (!this.tagSortedSpendsCache) this.tagSortedSpendsCache = {};
    let sorted = this.tagSortedSpendsCache[tagId];
    if (!sorted) {
      sorted = [];
      this.lotsValue.forEach((lot) => {
        const c = (lot.tag_costs && lot.tag_costs[tagId]) || 0;
        if (c > 0) sorted.push(c);
      });
      sorted.sort((a, b) => a - b);
      this.tagSortedSpendsCache[tagId] = sorted;
    }
    if (sorted.length === 0) return 4;
    let rank = 0;
    while (rank < sorted.length && sorted[rank] < cost) rank++;
    const denom = Math.max(sorted.length - 1, 1);
    return Math.min(4, Math.max(0, Math.round((rank / denom) * 4)));
  }

  #renderTooltipHtml(lot) {
    const popup = lot.popup;
    const labels = this.labelsValue.popup;
    if (!popup || !labels) {
      const sowLines = lot.noSowing
        ? `<div class="text-base-content/50">${escapeHtml(lot.noSowing)}</div>`
        : [lot.stageName, lot.weekLabel]
            .filter(Boolean)
            .map((line) => `<div class="text-base-content/70">${escapeHtml(line)}</div>`)
            .join("");
      return `
        <div class="text-xs leading-relaxed" style="min-width: 160px">
          <div class="font-semibold text-sm">${escapeHtml(lot.name)}</div>
          ${sowLines}
        </div>
      `;
    }
    const netClass = popup.net_result_negative ? "text-error" : "text-success";

    const rows = [
      [labels.net_result, popup.net_result_label, netClass],
      [labels.margin, popup.margin_label, null],
      [labels.sales, popup.income_label, null],
      [labels.direct_costs, popup.direct_cost_label, null],
      [labels.harvest, popup.harvest_label, null],
    ];

    if (this.activeLayerValue === "tag") {
      const tag = this.tagsById[this.activeTagIdValue];
      if (tag) {
        const tagId = this.activeTagIdValue;
        const formatted =
          (lot.tag_cost_labels && lot.tag_cost_labels[tagId]) ||
          lot.zero_tag_cost_label;
        const labelText = labels.tag_cost.replace("%{tag}", tag.name);
        rows.push([labelText, formatted, null]);
      }
    }

    const rowsHtml = rows
      .map(
        ([k, v, cls]) =>
          `<div class="flex justify-between gap-3"><span class="text-base-content/60">${escapeHtml(
            k,
          )}</span><span class="font-mono${cls ? ` ${cls}` : ""}">${escapeHtml(v)}</span></div>`,
      )
      .join("");

    return `
      <div class="text-xs leading-relaxed" style="min-width: 200px">
        <div class="font-semibold text-sm mb-1.5">${escapeHtml(popup.name)}</div>
        <div class="space-y-0.5">${rowsHtml}</div>
      </div>
    `;
  }
}

// Returns an HSL color using the tag's hue, with lightness pinned to one of
// five discrete steps so quintile ranks are visually distinct.
function tagColorAtBucket(hex, bucket) {
  const rgb = hexToRgbTriplet(hex);
  if (!rgb) return hex;
  const [h, s] = rgbToHsl(rgb[0], rgb[1], rgb[2]);
  const lightness = TAG_LIGHTNESS_STEPS[Math.max(0, Math.min(4, bucket))];
  const saturation = Math.max(s, 70);
  return `hsl(${Math.round(h)} ${Math.round(saturation)}% ${lightness}%)`;
}

function hexToRgbTriplet(hex) {
  if (!hex) return null;
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!match) return null;
  return [
    parseInt(match[1], 16),
    parseInt(match[2], 16),
    parseInt(match[3], 16),
  ];
}

function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }
  return [h, s * 100, l * 100];
}
